import { Popover, PopoverContent } from "@shared-components/shadcn/popover";
import { api } from "@shared-server/api";
import { type AppRouterOutput } from "@shared-server/api/app-router";
import { useDebounce } from "@shared-utils/hooks/use-debounce";
import { useState } from "react";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { listItemVariants } from "@shared-components/list-item";
import { LoaderEllipsis } from "@shared-components/loader-ellipsis";
import { Input } from "@shared-components/shadcn/input";
import { Label } from "@shared-components/shadcn/label";
import { useListKeyNavigation } from "@shared-utils/hooks/use-list-key-navigation";
import { Button } from "@shared-components/shadcn/button";
import { ChevronRight } from "lucide-react";
import { cn } from "@shared-utils/ui";

export type Department =
  AppRouterOutput["adminDepartments"]["autoCompleteDepartmentNames"][0];

interface DepartmentSelectProps {
  className?: string;
  onSelect: (adUser: Department) => void;
}

const minQueryLength = 3;

export const DepartmentSelect = (
  props: React.PropsWithChildren<DepartmentSelectProps>,
) => {
  // Local state
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce<string>(query, 300);

  // Query
  const searchDepartments =
    api.adminDepartments.autoCompleteDepartmentNames.useQuery(
      { name: debouncedQuery },
      { enabled: debouncedQuery.length >= minQueryLength },
    );

  // List navigation
  const keyListNav = useListKeyNavigation({
    items: searchDepartments.data ?? [],
    onEnter: (department) => {
      if (department) {
        selectItem(department);
      }
    },
  });

  // Callbacks
  const selectItem = (item: Department) => {
    props.onSelect(item);
    setQuery("");
  };

  const openPopover = Boolean(query !== "") && query.length >= minQueryLength;

  return (
    <div className="relative">
      <Label>{"Search departments"}</Label>
      <Input
        className="w-[200px]"
        placeholder={"Search departments..."}
        onKeyDown={keyListNav.onKeyDown}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <NestedDepartmentsPopover
        openPopover={openPopover}
        onInteractionOutside={() => setQuery("")}
        isLoading={searchDepartments.isLoading}
        departments={searchDepartments.data ?? []}
        onSelect={selectItem}
        selectedItem={keyListNav.selectedItem}
      />
      {props.children}
    </div>
  );
};

type NestedPopoverProps = {
  openPopover: boolean;
  onInteractionOutside: () => void;
  isLoading: boolean;
  departments: Department[];
  onSelect: (department: Department) => void;
  selectedItem: Department | undefined;
};

const NestedDepartmentsPopover = (props: NestedPopoverProps) => {
  const [useDepartmentChildren, setUseDepartmentChildren] = useState<
    string | null
  >(null);
  const { data, isLoading: isChildrenDepartmentsLoading } =
    api.adminDepartments.getChildrenDepartments.useQuery(
      {
        parentId: useDepartmentChildren ?? "",
      },
      {
        enabled: Boolean(useDepartmentChildren),
      },
    );

  const listItemClasses = listItemVariants({});
  const isLoading = props.isLoading || isChildrenDepartmentsLoading;

  return (
    <Popover open={props.openPopover}>
      <PopoverAnchor />
      <PopoverContent
        align="start"
        onInteractOutside={props.onInteractionOutside}
        className="max-h-[300px] overflow-y-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {isLoading && (
          <div className="p-2 min-w-[100%] min-h-2vh">
            <LoaderEllipsis className="*:bg-ter-green-400" />
          </div>
        )}
        {!isLoading && props.departments?.length === 0 && (
          <div className="p-2 text-center text-sm text-muted-foreground">
            {"Departments not found"}
          </div>
        )}
        {props.departments?.map((department) => (
          <div
            className={cn(
              listItemClasses,
              "flex flex-row justify-start gap-1 border-b-1 hover:cursor-pointer",
            )}
            onClick={() => props.onSelect(department)}
            key={department?.id}
          >
            <span className="inline-block flex-4/5">{department.name}</span>
            {department.hasChildren && (
              <Button
                variant={
                  department.id === useDepartmentChildren
                    ? "default"
                    : "outline"
                }
                size="xs"
                className="pl-1 flex-1/5"
                onClick={(e) => {
                  e.stopPropagation();
                  setUseDepartmentChildren(department.id);
                }}
              >
                <ChevronRight size={14} />
              </Button>
            )}
            {useDepartmentChildren === department.id && (
              <NestedDepartmentsPopover
                openPopover={Boolean(useDepartmentChildren)}
                onInteractionOutside={() => setUseDepartmentChildren(null)}
                isLoading={isLoading}
                departments={data ?? []}
                onSelect={props.onSelect}
                selectedItem={department}
              />
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};
